"use client"
import classNames from "classnames";
import React, { useMemo } from 'react';
import PosterImage from "src/components/posterrow/PosterImage";
import { getStreamingGuideUrl } from "src/lib/buildFilmUrl";
import { type Movie, type StreamingContent, type WatchableContent } from 'src/lib/movieinfo/movieinfotypes';
import { isMovie, isStreamingContent, isWatchableContent } from "src/lib/movieinfo/typeGuards";
import type { IFilmomtale } from 'src/lib/types/Filmomtale';
import cssPosterStyles from "./CSSPoster.module.css";
import withWatchlist from "src/components/filmwebid/withWatchlist";

//#region [Props]
export type CustomizablePosterProps = {
    className?: string;
    film: IFilmomtale | Movie | StreamingContent | WatchableContent;
    sizes?: string;
    children?: React.ReactNode;
    posterMissingText?: string;
    showMovieTitleIfPosterIsMissing?: boolean;
    hideTitle?: boolean;
    width?: number;
    optionalDisplay?: "year";
    longTitle?: boolean;
    hideKinoklubbBanner?: boolean;
};
//#endregion

//#region [Component]
export default function CustomizablePoster({ film, className, sizes, posterMissingText = "Plakat mangler", showMovieTitleIfPosterIsMissing = false, hideTitle = false, width, optionalDisplay, longTitle = false, hideKinoklubbBanner = false, children }: CustomizablePosterProps) {
    const [_filmUrl, _hasPoster, _isKinoklubb] = useMemo<[string | null, boolean, boolean]>(() => {
        if (!film) {
            return [null, false, false];
        }
        let url = "";
        let hasPoster = false;
        let isKinoklubb = false;
        if (isMovie(film)) {
            url = `/film/${film.mainVersionId}`
            hasPoster = !!film.sanityImagePosterUrl;
            isKinoklubb = film.isKinoklubb;
        } else if (isStreamingContent(film)) {
            url = getStreamingGuideUrl(film.id, film.isSeries);
            hasPoster = !!film.sanityImagePosterUrl || (film.imagesPoster?.length ?? 0) > 0;
        } else if (isWatchableContent(film)) {
            url = film.isCinemaRelevant ?
                `/film/${film.movieId}`
                : getStreamingGuideUrl(film.streamingContentId, film.isSeries);
            hasPoster = !!film.sanityImagePosterUrl || (film.imagesPosterStreaming?.length ?? 0) > 0;
        } else {
            url = `/film/${film.mainVersionEDI}`
            hasPoster = (film.postersV2?.length ?? 0) > 0 || !!film.mainPosterV2;
            isKinoklubb = !!film.isKinoklubb;
        }
        if (!url) {
            return [null, false, false];
        }
        return [url, hasPoster, isKinoklubb];
    }, [film])

    if (!_filmUrl) return null;

    return <div className={classNames(className, cssPosterStyles.CSSPoster) } >
        <PosterImage film={film} hasPoster={_hasPoster} showMovieTitleIfPosterIsMissing={showMovieTitleIfPosterIsMissing} filmUrl={_filmUrl} isKinoklubb={_isKinoklubb} hideKinoklubbBanner={hideKinoklubbBanner} posterMissingText={posterMissingText} sizes={sizes} width={width} />

        {React.Children.map(children, child => {
            if (React.isValidElement(child)) {
                return React.cloneElement(child, { film, filmUrl: _filmUrl } as { filmUrl: string; film: IFilmomtale | Movie | StreamingContent | WatchableContent; });
            }
            return child;
        })}

    </div>
}
//#endregion

export const CustomizablePosterWithWatchlist = withWatchlist(CustomizablePoster, "film");